// TAJMAC-PILOT — thread-list scoping for the MidasPilot role
// (MIDAS_PILOT_ROLE_SPEC.md §5; defense-in-depth / mode purity).
//
// Tenant isolation already bounds WHICH data a pilot can reach — nothing here is a
// data-leak fix. What these helpers close is the "platform mode" MODE itself: a pilot
// must only ever see machine-scoped chats, never fleet-wide ones.
//
// Why client-side filtering is needed even though the send path is now guarded:
//   • The thread lists are fetched UNFILTERED on two paths — `midasPageSlice.fetchThreads()`
//     (the V3 rail + MidasChatAreaV3's rehydrate) and `midasHistoryPageSlice`'s initial
//     load, which starts at `machineFilter: "all"`.
//   • Any platform thread that ALREADY EXISTS (e.g. created before the send guard landed,
//     or by an admin who later had the role changed) would otherwise be listed and
//     openable.
//   • It also covers the window before `machineContext` is populated, where a wire-level
//     machine filter has no id to send yet.
//
// Reads the role from `accountContext` (the JWT) rather than the account slice, matching
// PilotRouteGuard / Layout / ccSurface — it is available before any slice hydrates.
import accountContext from "../../account/accountContext";
import { Roles } from "../../account/models/AccountModels";

/** True when the signed-in user holds the MidasPilot role. */
export const isMidasPilotUser = (): boolean =>
  accountContext.user?.role === Roles.MIDAS_PILOT;

/**
 * THE STRUCTURAL RULE (2026-08-19 consolidation): for a MidasPilot, every thread /
 * history request must leave the browser ALREADY machine-scoped. We do NOT fetch the
 * fleet-wide set and filter it on arrival.
 *
 * Fetch-all-then-filter produced a whole class of bugs, three rounds running, all in the
 * seam between the fetch and the filter: `.unwrap()` consumers reading the unfiltered
 * payload, an unfiltered request racing and overwriting a scoped one, and server
 * pagination (`total` / `hasMore` / offsets) computed over rows the client then threw
 * away. Scoping at the wire deletes the seam instead of patching it.
 *
 * Consequence: a pilot with no `machineContext` yet is NOT YET ELIGIBLE to fetch — it is
 * a "wait" state, never an "empty" one. Auto-scope (MidasV3Layout) normally resolves it
 * in well under a second. Callers must therefore treat deferral as pending, and must
 * re-run once `machineContext` lands (i.e. keep it in the effect deps).
 *
 * Returns `machineId: null` for non-pilots, whose request shapes are byte-identical.
 */
export const getPilotWireScope = (
  rootState: unknown
): { isPilot: boolean; machineId: string | null } => {
  if (!isMidasPilotUser()) return { isPilot: false, machineId: null };
  const machineId =
    (rootState as { chatBot?: { machineContext?: { machineId?: string } | null } })
      ?.chatBot?.machineContext?.machineId ?? null;
  return { isPilot: true, machineId };
};

/**
 * True when a pilot thread/history fetch must be DEFERRED (pilot, but no machine scope
 * resolved yet). Use as a `createAsyncThunk` `condition` so the thunk is aborted before
 * `pending` — no request, no state write, no `hasHydrated` flip, no empty list stored.
 */
export const shouldDeferPilotThreadFetch = (rootState: unknown): boolean => {
  const { isPilot, machineId } = getPilotWireScope(rootState);
  return isPilot && !machineId;
};

/**
 * Drop platform (`machine_id === null`) threads from a pilot-visible list.
 *
 * SAFETY NET ONLY since the 2026-08-19 consolidation. Every pilot thread/history request
 * is now machine-scoped at the wire (see `getPilotWireScope`), so on a correctly scoped
 * response this call is a NO-OP — the server cannot return a `machine_id: null` row for a
 * `?machine_id=<guid>` query. It is kept, not removed, so that a future regression in the
 * wire scoping degrades to "correct but wasteful" instead of "leaks platform threads".
 * If you ever see it actually drop a row, the wire scoping upstream is broken — fix that,
 * don't lean on this.
 *
 * Returns the input array unchanged for every other role — same reference, so this cannot
 * perturb non-pilot memoisation or re-render behaviour.
 *
 * Optimistic rows are preserved regardless: they are the user's own in-flight send, and
 * the sendMessage guard already guarantees a pilot's send carries a machine id.
 */
export const filterThreadsForPilot = <
  T extends { machine_id: string | null; isOptimistic?: boolean }
>(
  threads: T[]
): T[] => {
  if (!isMidasPilotUser()) return threads;
  return threads.filter((t) => t.machine_id !== null || t.isOptimistic === true);
};
